import { stateWord } from "@lafayette/domain";
import type { StaffItineraryOutcome } from "@lafayette/schemas";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useWideLayout } from "../../layout/shell-layout";
import { AreaLink } from "../../navigation/AreaLink";
import { areaPath } from "../../navigation/offering";
import { emptyNoteClass, linkClass } from "../../requests/components/parts";
import { AsyncState } from "../../ui/async-state";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { BackLink } from "../../ui/links";
import { Notice as Message } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { lookOf, StatusLook } from "../../ui/status-look";
import { useTripCapabilities } from "../access";
import { useItineraryCommands } from "../commands";
import {
  AddItemDialog,
  EditItemDialog,
  FixedDialog,
  MoveItemDialog,
  RemoveItemDialog,
} from "../components/ItemDialogs";
import {
  ActivateDialog,
  AddDayDialog,
  ArchiveDialog,
  NewDraftDialog,
} from "../components/PlanDialogs";
import { ChangeHistory, ChecksPanel, RevisionList } from "../components/SidePanels";
import { isEditable, type PlanContext } from "../components/save";
import { type ItemAction, Timeline } from "../components/Timeline";
import { describeItineraryError } from "../errors";
import { PLAN_STATUS_LOOK } from "../labels";
import { useItineraryDetail, useRequestStates, useTripRevisions } from "../queries";
import { useItineraryRuntime } from "../runtime";
import {
  activeRevision,
  buildDays,
  type Detail,
  defaultRevision,
  evidenceOf,
  planToken,
  type RunView,
  requestIdsOf,
  runItemsOf,
} from "../view-model";

// The staff itinerary page (CHT-747): `/trips/:tripId/itinerary` opens the plan of record (or the newest draft), and
// `/trips/:tripId/itinerary/:itineraryId` opens any revision, read-only when it is history. The page reads the plan, joins it with
// the catalog (is the item still published) and the request read path (the live state of a linked request), and offers the editor
// only to a role with `trip.manage` and only for a plan staff may still change (a draft or the plan of record). Every action is one
// of the domain commands with the version tokens of the plan the page read.

type DialogBody =
  | Readonly<{ kind: "add"; sectionId: string }>
  | Readonly<{ kind: "day" | "activate" | "archive" | "newDraft" }>
  | (ItemAction & Readonly<{ kind: "edit" | "move" | "remove" | "fixed" }>);

/**
 * An open dialog. `id` is its intent: the idempotency keys of what it sends belong to it and end with it. `snapshot` is the plan as
 * it was when the dialog opened: its version tokens are the only ones the dialog sends, and the item it edits was read from it.
 */
type Dialog = DialogBody & Readonly<{ id: string; snapshot: Detail | null }>;

const newIntent = (): string => globalThis.crypto.randomUUID();

type Notice = Readonly<{ tone: "ok" | "error"; text: string }>;

const tripPath = (tripId: string, itineraryId?: string) =>
  `/trips/${encodeURIComponent(tripId)}/itinerary${itineraryId === undefined ? "" : `/${encodeURIComponent(itineraryId)}`}`;

export function ItineraryPage() {
  useWideLayout();
  const { tripId = "", itineraryId } = useParams();
  const { capabilities, isLoading: rolesLoading } = useTripCapabilities();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  // The notice lives here, not in `PlanView`: a new revision has a new id, so the view remounts and would drop it.
  const [notice, setNotice] = useState<Notice | null>(null);
  const navigate = useNavigate();
  const commands = useItineraryCommands();
  const revisions = useTripRevisions(tripId, capabilities.read);
  const rows = revisions.data;
  const target = itineraryId ?? (rows === undefined ? null : (defaultRevision(rows)?.id ?? null));
  const detail = useItineraryDetail(target, capabilities.read);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include trips and itineraries."
      />
    );
  }
  if (itineraryId === undefined && revisions.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the itinerary" message="Waiting for the answer." />
    );
  }
  if (itineraryId === undefined && revisions.isError) {
    const failure = describeItineraryError(revisions.error);
    return failure.kind === "forbidden" ? (
      <AsyncState kind="error" title="You cannot open this" message={failure.message} />
    ) : (
      <AsyncState
        kind="error"
        title="The itinerary did not load"
        message={failure.message}
        onRetry={() => void revisions.refetch()}
      />
    );
  }
  if (itineraryId === undefined && rows !== undefined && rows.length === 0) {
    return (
      <section className="grid gap-5">
        <div>
          <AreaLink area="concierge" as={BackLink} to={areaPath("concierge")}>
            Back to the workspace
          </AreaLink>
        </div>
        <PageHeader
          eyebrow={<span className="font-bold tracking-[0.14em] text-link">Itinerary</span>}
          title="No itinerary yet"
          description="This trip has no plan. A staff draft stays hidden from the customer until you activate it."
        />
        {capabilities.manage ? (
          <div>
            <Button
              onClick={() => setDialog({ kind: "newDraft", id: newIntent(), snapshot: null })}
            >
              Start a staff draft
            </Button>
          </div>
        ) : null}
        {dialog?.kind === "newDraft" ? (
          <NewDraftDialog
            key={dialog.id}
            tripId={tripId}
            run={(request) => commands.run(request, dialog.id)}
            onDone={(outcome) => {
              commands.dropIntent(dialog.id);
              setDialog(null);
              void navigate(tripPath(tripId, outcome.itineraryId), { replace: true });
            }}
            onClose={() => {
              commands.dropIntent(dialog.id);
              setDialog(null);
              commands.reloadAfterClose();
            }}
          />
        ) : null}
      </section>
    );
  }
  if (detail.isPending) {
    return (
      <AsyncState kind="loading" title="Loading the itinerary" message="Waiting for the answer." />
    );
  }
  if (detail.isError) {
    const failure = describeItineraryError(detail.error);
    return failure.kind === "forbidden" ? (
      <AsyncState kind="error" title="You cannot open this" message={failure.message} />
    ) : (
      <AsyncState
        kind="error"
        title="The itinerary did not load"
        message={failure.message}
        onRetry={() => void detail.refetch()}
      />
    );
  }

  return (
    <PlanView
      key={detail.data.itinerary.id}
      tripId={tripId}
      detail={detail.data}
      revisionRows={rows ?? detail.data.revisions}
      canManage={capabilities.manage}
      busy={detail.isFetching}
      dialog={dialog}
      setDialog={setDialog}
      notice={notice}
      setNotice={setNotice}
      commands={commands}
    />
  );
}

type PlanViewProps = Readonly<{
  tripId: string;
  detail: Detail;
  revisionRows: Parameters<typeof RevisionList>[0]["rows"];
  canManage: boolean;
  busy: boolean;
  dialog: Dialog | null;
  setDialog: (dialog: Dialog | null) => void;
  notice: Notice | null;
  setNotice: (notice: Notice | null) => void;
  commands: ReturnType<typeof useItineraryCommands>;
}>;

function PlanView({
  tripId,
  detail,
  revisionRows,
  canManage,
  busy,
  dialog,
  setDialog,
  notice,
  setNotice,
  commands,
}: PlanViewProps) {
  const { now } = useItineraryRuntime();
  const navigate = useNavigate();
  const noticeRef = useRef<HTMLDivElement>(null);
  const itinerary = detail.itinerary;

  useEffect(() => {
    if (notice !== null) noticeRef.current?.focus();
  }, [notice]);

  // The answer of the last full validation belongs to the version it ran for; another version hides it.
  const [lastRun, setLastRun] = useState<Readonly<{ token: string; run: RunView }> | null>(null);
  const days = useMemo(() => buildDays(detail), [detail]);
  const requests = useRequestStates(requestIdsOf(detail.items));
  const evidence = evidenceOf(detail, days, now());
  const run = lastRun !== null && lastRun.token === planToken(detail) ? lastRun.run : null;
  const runItems = useMemo(() => (run === null ? null : runItemsOf(run)), [run]);
  const current = activeRevision(revisionRows);
  const editable = canManage && isEditable(detail);
  const firstDay =
    detail.sections.find((section) => section.kind === "DAY") ?? detail.sections[0] ?? null;

  // The dialog works on the plan it was opened on. When the page reads another version (a background read after another staff
  // member's edit), the dialog says so and sends nothing.
  const snapshot = dialog?.snapshot ?? detail;
  const stale = dialog !== null && planToken(snapshot) !== planToken(detail);
  const intent = dialog?.id ?? "";
  const context: PlanContext = {
    detail: snapshot,
    stale,
    tripId,
    run: (request) => commands.run(request, intent),
  };
  const dialogRef = useRef(dialog);
  dialogRef.current = dialog;

  const openDialog = (body: DialogBody) => {
    setNotice(null);
    setDialog({ ...body, id: newIntent(), snapshot: detail });
  };

  const close = () => {
    if (dialog !== null) commands.dropIntent(dialog.id);
    setDialog(null);
    commands.reloadAfterClose();
  };

  /** A completion belongs to the dialog that started it. If another dialog is open (or none), the answer is obsolete and ignored. */
  const forDialog =
    <T,>(done: (outcome: T) => void) =>
    (outcome: T) => {
      if (dialogRef.current === null || dialogRef.current.id !== intent) return;
      commands.dropIntent(intent);
      done(outcome);
    };

  /** After a command: close the dialog, say what happened, and open the plan the command produced (a new revision has a new id). */
  const finish = (text: string) => (outcome: StaffItineraryOutcome) => {
    setDialog(null);
    const blocking = outcome.blockingCount ?? 0;
    const warnings = outcome.warningCount ?? 0;
    const counts = [
      blocking > 0 ? `${blocking} blocking ${blocking === 1 ? "problem" : "problems"}` : null,
      warnings > 0 ? `${warnings} ${warnings === 1 ? "warning" : "warnings"}` : null,
    ].filter((part): part is string => part !== null);
    setNotice({
      tone: "ok",
      text: counts.length > 0 ? `${text} It has ${counts.join(" and ")}: see Checks.` : text,
    });
    if (outcome.itineraryId !== itinerary.id) {
      void navigate(tripPath(tripId, outcome.itineraryId), { replace: true });
    }
  };

  const savedText = (outcome: StaffItineraryOutcome): string =>
    itinerary.status === "DRAFT"
      ? "Draft updated."
      : outcome.status === "DRAFT"
        ? "Proposal saved as a staff-only draft. The customer sees nothing until you activate it."
        : `Change applied. The current plan is now revision ${outcome.revision ?? ""}.`.replace(
            "revision .",
            "the new revision.",
          );

  const onSaved = forDialog((outcome: StaffItineraryOutcome) =>
    finish(savedText(outcome))(outcome),
  );

  async function validate(mode: "FULL" | "STRUCTURAL") {
    setNotice(null);
    const result = await commands.validate(itinerary.id, {
      mode,
      expectedVersion: itinerary.version,
    });
    if (!result.ok) {
      setNotice({ tone: "error", text: result.failure.message });
      return;
    }
    if (mode === "STRUCTURAL") {
      setNotice({
        tone: "ok",
        text: "Structural check finished. It looks at the structure and the times only. It did not check routes or weather, and it did not refresh that evidence.",
      });
      return;
    }
    const answered = result.result.run;
    if (answered === undefined) {
      setNotice({
        tone: "ok",
        text: "This server does not run the route and weather checks, so only the structure and the times were checked. The evidence below is unchanged.",
      });
      return;
    }
    setLastRun({ token: planToken(detail), run: answered });
    setNotice({
      tone: "ok",
      text: answered.stale
        ? "The plan changed while the checks ran. Nothing was recorded. Read the plan and run the validation again."
        : "Full validation finished. See Checks and each item.",
    });
  }

  const onAction = (action: ItemAction) => {
    if (action.kind === "add") openDialog({ kind: "add", sectionId: action.section.id });
    else openDialog(action);
  };

  const status = stateWord("itinerary", "staffShort", itinerary.status);
  const statusLook = lookOf(PLAN_STATUS_LOOK, itinerary.status);
  const history = itinerary.status === "SUPERSEDED" || itinerary.status === "ARCHIVED";
  const hasDay = detail.sections.length > 0;

  return (
    <section className="grid gap-5" aria-busy={busy}>
      <div>
        <AreaLink area="concierge" as={BackLink} to={areaPath("concierge")}>
          Back to the workspace
        </AreaLink>
      </div>
      <PageHeader
        eyebrow={<span className="font-bold tracking-[0.14em] text-link">Itinerary</span>}
        title={itinerary.title === "" ? "Untitled itinerary" : itinerary.title}
        description={
          <span className="flex flex-wrap items-center gap-1.5 pt-1">
            <StatusLook look={statusLook}>{status}</StatusLook>
            <Badge>Revision {itinerary.revision}</Badge>
            <Badge>Version {itinerary.version}</Badge>
            <Badge>
              {itinerary.createdBy === "STAFF" ? "Created by staff" : "Created by the customer"}
            </Badge>
          </span>
        }
        actions={
          editable ? (
            <>
              {hasDay ? (
                <Button
                  onClick={() => {
                    if (firstDay !== null) openDialog({ kind: "add", sectionId: firstDay.id });
                  }}
                >
                  Add item
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => openDialog({ kind: "day" })}>
                Add day
              </Button>
              {itinerary.status === "DRAFT" ? (
                <Button onClick={() => openDialog({ kind: "activate" })}>Activate plan</Button>
              ) : null}
              <Button
                variant="outline"
                className="border-destructive-border text-destructive"
                onClick={() => openDialog({ kind: "archive" })}
              >
                {itinerary.status === "DRAFT" ? "Discard draft" : "Withdraw plan"}
              </Button>
            </>
          ) : null
        }
      />

      {history ? (
        <Message tone="info" role="note">
          <p>
            <strong>A past revision.</strong> It is kept as history and cannot be changed.{" "}
            {current === null ? (
              "The trip has no current plan."
            ) : (
              <Link className={linkClass} to={tripPath(tripId, current.id)}>
                Open the current plan (revision {current.revision})
              </Link>
            )}
          </p>
        </Message>
      ) : itinerary.status === "DRAFT" ? (
        <Message tone="info" role="note">
          <p>
            {itinerary.createdBy === "STAFF"
              ? "A staff draft. The customer cannot see it until you activate it."
              : "A customer draft. The customer can see and change it too."}
            {current === null ? (
              ""
            ) : (
              <>
                {" "}
                <Link className={linkClass} to={tripPath(tripId, current.id)}>
                  Open the current plan (revision {current.revision})
                </Link>
              </>
            )}
          </p>
        </Message>
      ) : (
        <Message tone="info" role="note">
          <p>
            The current plan. An edit makes a new revision; the earlier one stays in the history.
          </p>
        </Message>
      )}

      {notice === null ? null : (
        <Message
          focusRef={noticeRef}
          tone={notice.tone === "ok" ? "success" : "warning"}
          role={notice.tone === "ok" ? "status" : "alert"}
        >
          <p>{notice.text}</p>
        </Message>
      )}

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,21rem)]">
        <div className="grid min-w-0 gap-4">
          <h2 className="m-0 font-serif text-[28px] leading-[34px] font-medium">Plan</h2>
          {detail.items.length === 0 && detail.sections.length === 0 ? (
            <p className={emptyNoteClass}>
              This plan is empty. {editable ? "Add a day, then add items to it." : ""}
            </p>
          ) : (
            <Timeline
              days={days}
              requests={requests}
              run={runItems}
              now={now()}
              canEdit={editable}
              onAction={onAction}
            />
          )}
        </div>
        <StickyAside>
          <ChecksPanel
            detail={detail}
            evidence={evidence}
            run={run}
            canValidate={editable}
            validating={commands.pending}
            onValidate={(mode) => void validate(mode)}
          />
          <RevisionList tripId={tripId} rows={revisionRows} currentId={itinerary.id} />
          <ChangeHistory changes={detail.changes} />
        </StickyAside>
      </div>

      {dialog?.kind === "add" ? (
        <AddItemDialog
          context={context}
          sections={snapshot.sections}
          initialSectionId={dialog.sectionId}
          onDone={onSaved}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === "edit" ? (
        <EditItemDialog
          context={context}
          item={dialog.item}
          section={dialog.section}
          onDone={onSaved}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === "move" ? (
        <MoveItemDialog
          context={context}
          item={dialog.item}
          sections={snapshot.sections}
          from={dialog.section}
          onDone={onSaved}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === "remove" ? (
        <RemoveItemDialog context={context} item={dialog.item} onDone={onSaved} onClose={close} />
      ) : null}
      {dialog?.kind === "fixed" ? (
        <FixedDialog context={context} item={dialog.item} onDone={onSaved} onClose={close} />
      ) : null}
      {dialog?.kind === "day" ? (
        <AddDayDialog context={context} onDone={onSaved} onClose={close} />
      ) : null}
      {dialog?.kind === "activate" ? (
        <ActivateDialog
          detail={snapshot}
          stale={stale}
          run={context.run}
          onDone={forDialog(finish("Plan activated. The customer can see it now."))}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === "archive" ? (
        <ArchiveDialog
          detail={snapshot}
          stale={stale}
          run={context.run}
          onDone={forDialog(
            finish(snapshot.itinerary.status === "DRAFT" ? "Draft discarded." : "Plan withdrawn."),
          )}
          onClose={close}
        />
      ) : null}
    </section>
  );
}

/**
 * The Checks, the revisions and the history sit beside the plan and stay in view while a long plan scrolls (from 1280 px wide), below the sticky
 * top bar of the shell (3.5 rem). When
 * they are taller than the window they scroll inside their own box: the box then takes the keyboard focus, so a keyboard user can scroll
 * it too. When they fit, it is a plain landmark with no tab stop.
 */
function StickyAside({ children }: Readonly<{ children: React.ReactNode }>) {
  const box = useRef<HTMLElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const node = box.current;
    if (node === null || typeof ResizeObserver === "undefined") return;
    const measure = () => setScrolls(node.scrollHeight > node.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    for (const child of node.children) observer.observe(child);
    return () => observer.disconnect();
  }, []);
  return (
    <aside
      ref={box}
      aria-label="Checks and history"
      {...(scrolls ? { tabIndex: 0 } : {})}
      className="grid min-w-0 content-start gap-4 xl:sticky xl:top-[4.5rem] xl:max-h-[calc(100dvh-5.5rem)] xl:overflow-y-auto"
    >
      {children}
    </aside>
  );
}
